import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Select, Tooltip, notification } from "antd";
import OBCSearchInput from "../OBC/OBCSearchInput";
import "../../style/utuSuspectsTab.scss";
import {
    getUtuSuspects,
    setUTUSuspectSearchQueryFilter,
    selectUTUSuspect,
    routeSelected,
    changeToolMode,
    routeHighlighted,
    resetStillImageAdjustments,
    railInspectionExited,
    updateZoom,
    setUTUSuspectDateFilter,
} from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { VariableSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useWindowResize } from "../Issues/IssuesTab";
import OBCSpinner from "../util/OBC";
import _ from "lodash";
import { calculatePulseFromTimestamp } from "../util/Geometry";
import DayPicker from "react-day-picker";
import OBCButton from "../OBC/OBCButton";
import { CalendarOutlined } from "@ant-design/icons";
import moment from "moment";
import { faExclamationCircle, faSortAmountDown } from "@fortawesome/free-solid-svg-icons";

const utuSuspectsSelector = (state) => state.utuSuspects.data;
const selectedSuspectSelector = (state) => state.utuSuspects.selectedSuspect;
const searchQueryFilterSelector = (state) => state.utuSuspects.searchQueryFilter;
const dateFiltersSelector = (state) => state.utuSuspects.dateFilter;
const suspectsCountSelector = (state) => state.utuSuspects.count;

const GUTTER_SIZE = 30;
const ISSUE_ITEM_HEIGHT_CLOSED = 200;

const SORT_OPTIONS = [
    // {
    //     value: "last_observed",
    //     label: "Last Observed",
    //     short_title: "Most Recent",
    //     description: "newest faults first",
    // },
    {
        value: "high_priority",
        label: "Highest Priority",
        short_title: "Priority",
        description: "highest priority faults first",
    },
    {
        value: "low_priority",
        label: "Low Priority",
        short_title: "Low Priority",
        description: "lowest priority faults first",
    },
];

const UtuSuspectsTab = () => {
    const dispatch = useDispatch();
    const utuSuspects = useSelector(utuSuspectsSelector);
    const listRef = useRef();
    const sizeMap = useRef({});
    const [windowWidth] = useWindowResize();
    const [gettingSuspects, setGettingSuspects] = useState(true);
    const selectedSuspect = useSelector(selectedSuspectSelector);
    const [sortValue, setSortValue] = useState("high_priority");
    const [sortDescription, setSortDescription] = useState("highest priority faults first");
    const searchQuery = useSelector(searchQueryFilterSelector);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const dateFilter = useSelector(dateFiltersSelector);
    const dateFrom = dateFilter.from ? new Date(dateFilter.from * 1000) : null;
    const dateTo = dateFilter.to ? new Date(dateFilter.to * 1000) : null;
    const modifiers = { start: dateFrom, end: dateTo };
    const totalAmountSuspects = useSelector(suspectsCountSelector);

    const onSearchQueryChanged = (value) => {
        dispatch(setUTUSuspectSearchQueryFilter(value));
    };

    useEffect(() => {
        dispatch(getUtuSuspects(sortValue, searchQuery, dateFilter.from, dateFilter.to));
        setGettingSuspects(false);
        let sort_description = _.find(SORT_OPTIONS, { value: sortValue });
        setSortDescription(sort_description.description);
    }, [dispatch, sortValue, searchQuery, dateFilter]);

    const renderDatePicker = () => {
        const today = new Date();
        return (
            <>
                <DayPicker
                    className="Selectable"
                    selectedDays={[dateFrom, { from: dateFrom, to: dateTo }]}
                    modifiers={modifiers}
                    onDayClick={handleDayClick}
                    disabledDays={{ after: today }}
                />
                <div className="DatePickerButtons">
                    <OBCButton
                        className="ButtonReset"
                        disabled={dateFilter.from ? false : true}
                        onClick={() => dispatch(setUTUSuspectDateFilter({ from: null, to: null }))}>
                        Reset
                    </OBCButton>
                    <OBCButton
                        className="ButtonOk"
                        variant="secondary"
                        onClick={() => setDatePickerOpen(false)}>
                        Close
                    </OBCButton>
                </div>
            </>
        );
    };

    const sortByOptions = useMemo(() => {
        let options = [];

        options = SORT_OPTIONS.map((option) => (
            <Select.Option
                key={option.value}
                value={option.value}
                label={
                    <>
                        <FontAwesomeIcon
                            icon={faSortAmountDown}
                            style={{ marginRight: "5px" }}
                        />
                        {option.short_title}
                    </>
                }>
                {option.label}
            </Select.Option>
        ));

        return options;
    }, []);

    const handleDayClick = (day) => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (day > today) {
            return;
        }

        const currentRange = {
            from: new Date(dateFilter.from * 1000),
            to: new Date(dateFilter.to * 1000),
        };

        const range = DayPicker.DateUtils.addDayToRange(day, currentRange);
        if (!dateFilter.to) {
            range.to = day;
            range.from = day;
        }
        const newDateFilter = {
            to: moment(range.to).endOf("day").unix(),
            from: moment(range.from).startOf("day").unix(),
        };

        dispatch(setUTUSuspectDateFilter(newDateFilter));
    };

    const setSize = useCallback((index, size) => {
        sizeMap.current = { ...sizeMap.current, [index]: size };
        listRef.current.resetAfterIndex(index);
    }, []);

    const getItemSize = (index) => {
        if (sizeMap.current[index]) {
            return Math.max(sizeMap.current[index] + GUTTER_SIZE, ISSUE_ITEM_HEIGHT_CLOSED);
        }
        return ISSUE_ITEM_HEIGHT_CLOSED;
    };

    const Row = useCallback(
        ({ index, style }) => {
            const suspectID = utuSuspects[index]?.uid || null;
            const suspect = utuSuspects[index] || [];

            return (
                <div
                    style={{
                        ...style,
                        left: style.left + 2,
                    }}>
                    <SuspectItem
                        key={`${suspectID}`}
                        index={index}
                        suspect={suspect}
                        setSize={setSize}
                        selected={suspectID === selectedSuspect}
                        minHeight={ISSUE_ITEM_HEIGHT_CLOSED}
                    />
                </div>
            );
        },
        [utuSuspects, setSize, selectedSuspect],
    );

    return (
        <div className="utuSuspectsContentTab">
            <div className="TopContainer">
                <div className="utuFilterContainer">
                    <div className="LeftColumn">
                        <Tooltip title="Sort suspects">
                            <div className="SortByPrioritySelect">
                                <span>Sort</span>
                                <Select
                                    optionLabelProp="label"
                                    value={sortValue}
                                    onChange={setSortValue}
                                    dropdownMatchSelectWidth={false}>
                                    {sortByOptions}
                                </Select>
                            </div>
                        </Tooltip>
                        <Tooltip title="Filter suspects by date range">
                            <div className="DateFilterSelect">
                                <span>Filter</span>
                                <Tooltip
                                    overlayClassName="IssueTabDatePicker"
                                    overlayStyle={{ opacity: 1 }}
                                    title={renderDatePicker(dateFrom, dateTo, modifiers)}
                                    visible={datePickerOpen}
                                    placement="bottomLeft"
                                    trigger="click"
                                    onVisibleChange={(visible) => setDatePickerOpen(visible)}>
                                    <div
                                        className="sessionFilterItem FilterItemDatePicker"
                                        style={{ background: "#414664" }}>
                                        <OBCButton
                                            size="lg"
                                            active={datePickerOpen}
                                            variant={dateFilter.from ? "primary" : "ghost"}
                                            onClick={() => setDatePickerOpen(!datePickerOpen)}
                                            aria-label="Calendar">
                                            <CalendarOutlined
                                                className="icon"
                                                aria-hidden="true"
                                            />
                                        </OBCButton>
                                    </div>
                                </Tooltip>
                            </div>
                        </Tooltip>
                    </div>
                    <div className="RightColumn">
                        <OBCSearchInput
                            className="UTUSuspectsFilterSearchInput"
                            placeholder="Search suspects"
                            onChange={onSearchQueryChanged}
                            value={searchQuery}
                        />
                    </div>
                </div>
                <div className="CountLabel">
                    Showing <span className={`${utuSuspects.length && "Value"}`}>{utuSuspects.length}</span> of {totalAmountSuspects} ultrasonic suspects,
                    sorted by {sortDescription}
                </div>
            </div>
            {gettingSuspects ? (
                <div className="UTUTabLoading">
                    <OBCSpinner
                        size={70}
                        speed={3}
                        color={"#e8dfff"}
                    />
                </div>
            ) : (
                <div
                    className="UtuSuspectsList"
                    id="ScrollList">
                    <AutoSizer className="suspectsAutosizer">
                        {({ height, width }) => (
                            <VariableSizeList
                                height={height}
                                width={width - 5}
                                style={{ overflowX: "hidden" }}
                                itemSize={getItemSize}
                                ref={listRef}
                                itemCount={utuSuspects.length}>
                                {Row.bind(this)}
                            </VariableSizeList>
                        )}
                    </AutoSizer>
                </div>
            )}
        </div>
    );
};

const SuspectItem = ({ index, suspect, setSize, windowWidth, minHeight, selected }) => {
    const dispatch = useDispatch();
    const ref = useRef();
    const [UIDCopyText, setUIDCopyText] = useState("Click to copy");
    const [UIDHovered, setUIDHovered] = useState(false);

    const setIssueSize = () => {
        const size = ref.current.getBoundingClientRect().height - 20;
        setSize(index, size);
    };

    useEffect(() => {
        setIssueSize();
    }, [setSize, index, windowWidth]);

    const openNotification = () => {
        notification.info({
            message: "No matched session",
            description: "There is no session to open as this fault has not been matched to one",
        });
    };

    const suspectStatus = () => {
        if (suspect.priority === 1) {
            let colour = "red";
            let icon = faExclamationCircle;
            let text = "High Priority";

            return (
                <div className={`StatusDiv`}>
                    <FontAwesomeIcon
                        icon={icon}
                        className={`Icon ${colour}`}
                    />
                    <p className="StatusText">{text}</p>
                </div>
            );
        }
    };

    const onSuspectItemClick = () => {
        if (!selected) {
            dispatch(selectUTUSuspect(suspect.uid));
            if (suspect.session_id) {
                dispatch(routeSelected(suspect.session_id, suspect.image_ts, undefined, undefined, 0));
                dispatch(routeHighlighted(null));
                dispatch(changeToolMode(null));
                dispatch(railInspectionExited());
                dispatch(resetStillImageAdjustments());
                dispatch(updateZoom(1));
                window.open(`https://view.aivr.video/rail-inspection/${suspect.session_id}/${suspect.image_ts}`);
            } else {
                openNotification();
            }
        }
    };

    const parseDate = (date) => {
        const dateList = date.split("-");
        return `${dateList[2]}/${dateList[1]}/${dateList[0]}`;
    };

    const handleUIDHovered = (e) => {
        if (e && UIDCopyText === "Copied!") {
            setUIDCopyText("Click to copy");
        }
        setUIDHovered(e);
    };

    const handleUIDClick = (e, id) => {
        e.preventDefault();
        navigator.clipboard.writeText(id);
        setUIDCopyText("Copied!");
    };

    return (
        <>
            <div
                className={"IssueListItem " + (selected ? " Open" : " Closed")}
                onClick={onSuspectItemClick}
                style={{ minHeight }}
                ref={ref}>
                <div className="SuspectHeader">
                    <Tooltip
                        title={UIDCopyText}
                        onVisibleChange={(e) => handleUIDHovered(e)}>
                        <div
                            className="SuspectIdContainer"
                            onClick={(e) => handleUIDClick(e, suspect.uid)}>
                            <div className="SuspectID">UID: {suspect.uid}</div>
                        </div>
                    </Tooltip>
                    {suspectStatus()}
                </div>
                <div className="SuspectBody">
                    <span>
                        <strong>Session ID: </strong>
                        {suspect.session_id ? suspect.session_id : "Not matched to a session"}
                    </span>
                    {suspect.priority === 1 ? (
                        <span>
                            <strong>Priority:</strong> <span className="highPriority">{suspect.priority}</span>
                        </span>
                    ) : (
                        <span>
                            <strong>Priority:</strong> {suspect.priority}
                        </span>
                    )}
                    <span>
                        <strong>Run Date:</strong> {parseDate(suspect.run_detail)}
                    </span>
                    <span>
                        <strong>Test Unit:</strong> {suspect.test_unit}
                    </span>
                    <span>
                        <strong>Pulse Count:</strong> {suspect.pulse_count}
                    </span>
                    <span>
                        <strong>Type:</strong> {suspect.suspect_type}
                    </span>
                    <span>
                        <strong>Comment:</strong> {suspect.suspect_comment}
                    </span>
                </div>
                <div className="SuspectFooter">
                    <div className="SuspectFooterContainer">
                        <div className="SuspectPosition">
                            <span>ELR: {suspect.elr} </span>
                            <span>Track: {suspect.track_id}</span>
                            <span>Miles: {suspect.miles}</span>
                            <span>Yards: {suspect.yards}</span>
                            <span>Rail: {suspect.rail}</span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UtuSuspectsTab;
